import {CushlaGetQueryOptions, endpointKeys} from '@common/api'
import type {
  BlockSlotRequest,
  UnblockSlotRequest,
  RescheduleSlotResponse,
  PartnerServiceSlotResponse,
  Availabilities,
} from '@generated/models'
import {
  rescheduleSlotResponseNormaliser,
  partnerServiceSlotResponseNormaliser,
} from '@common/normalisers/partnerAdmin'
import {availabilitiesNormaliser} from '@common/normalisers/booking'

import {useApi, useWebGet, useWebSet, useWebVariableUrlSet} from './helpers'

export const useBlockPartnerServiceSlot = (
  partnerServiceId: string,
  slotId: string,
) => {
  const api = useApi()
  return useWebSet<BlockSlotRequest, PartnerServiceSlotResponse>(
    [endpointKeys.partnerAdminPartnerServiceSlotBlock],
    api.partnerAdminPartnerServiceSlotBlock({
      partnerServiceId,
      partnerServiceSlotId: slotId,
    }),
    'PATCH',
    partnerServiceSlotResponseNormaliser,
    [
      [endpointKeys.partnerAdminSlotReport, slotId],
      [endpointKeys.partnerAdminPatientsReport],
    ],
  )
}

export const useUnblockPartnerServiceSlot = (
  partnerServiceId: string,
  slotId: string,
) => {
  const api = useApi()
  return useWebSet<UnblockSlotRequest, PartnerServiceSlotResponse>(
    [endpointKeys.partnerAdminPartnerServiceSlotUnblock],
    api.partnerAdminPartnerServiceSlotUnblock({
      partnerServiceId,
      partnerServiceSlotId: slotId,
    }),
    'PATCH',
    partnerServiceSlotResponseNormaliser,
    [
      [endpointKeys.partnerAdminSlotReport, slotId],
      [endpointKeys.partnerAdminPatientsReport],
    ],
  )
}

export const useCancelPartnerServiceSlot = (
  partnerServiceId: string,
  slotId: string,
) => {
  const api = useApi()
  return useWebVariableUrlSet<
    {partnerServiceId: string; partnerServiceSlotId: string; comment?: string},
    Record<string, never>,
    void
  >(
    [endpointKeys.partnerAdminPartnerServiceSlotCancel],
    ({partnerServiceId: serviceId, partnerServiceSlotId, comment}) => {
      const url = api.partnerAdminPartnerServiceSlotCancel({
        partnerServiceId: serviceId,
        partnerServiceSlotId,
      })
      if (comment) {
        url.searchParams.set('comments', comment)
      }
      return url
    },
    'DELETE',
    () => undefined,
    {},
    [
      [endpointKeys.partnerAdminPatientReport],
      [endpointKeys.partnerAdminSlotReport, slotId],
    ],
  )
}

export const useReschedulePartnerServiceSlot = (
  partnerServiceId: string,
  fromSlotId: string,
) => {
  const api = useApi()
  return useWebVariableUrlSet<
    {partnerServiceId: string; fromPartnerServiceSlotId: string},
    {date: string; time: string; comment?: string | null},
    RescheduleSlotResponse
  >(
    [endpointKeys.partnerAdminPartnerServiceSlotReschedule],
    ({partnerServiceId: serviceId, fromPartnerServiceSlotId}) =>
      api.partnerAdminPartnerServiceSlotReschedule({
        partnerServiceId: serviceId,
        fromPartnerServiceSlotId,
      }),
    'POST',
    rescheduleSlotResponseNormaliser,
    {},
    [
      [endpointKeys.partnerAdminPatientReport],
      [endpointKeys.partnerAdminSlotReport, fromSlotId],
    ],
  )
}

export const useGetPartnerServiceAvailabilities = (
  partnerServiceId: string,
  monthSpec: string,
  options?: CushlaGetQueryOptions<Availabilities>,
) => {
  const api = useApi()
  return useWebGet<Availabilities>(
    [
      endpointKeys.partnerAdminPartnerServiceAvailabilities,
      partnerServiceId,
      monthSpec,
    ],
    api.partnerAdminPartnerServiceAvailabilities({
      partnerServiceId,
      monthSpec,
    }),
    availabilitiesNormaliser,
    options ?? {},
  )
}
